import { Headline, Search, SegmentedControl } from '@velocity/ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../layout';
import { setNotification } from '../../../redux/cache-slice';
import {
  fetchDealersList,
  setSelectedDealer,
  updateCustomerData,
} from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { APIPaths, Dealer } from '../../../types/instant-lease-api';
import { haversineDistance } from '../../../utils/geo';
import { errorLogging } from '../../../utils/logging';
import Navigation from '../../../utils/navigation';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';
import DealerCard from './dealer-card';

type DealerTab = 'suggestions' | 'search';

// Figma: body/text-loose-200 in brand/secondary-400 (the global `p` colour is
// unlayered CSS, hence the important modifier).
const bodyTextClass = 'text-loose-200 m-0! text-(--vel-color-secondary-400)!';

// The dealer-search API can return multiple dealers that share the same `id`
// (e.g. the `smart` brand returns every dealer with id "3"). Build a composite
// identity so selection and React keys stay unique per physical location.
const getDealerKey = (
  dealer: Pick<Dealer, 'id' | 'lat' | 'lng' | 'street'>,
): string =>
  `${dealer.id}-${dealer.lat ?? ''}-${dealer.lng ?? ''}-${dealer.street}`;

const DealerSelection: React.FC = () => {
  const { activeApplication, dealers, selectedDealer, carData } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  const { accessToken } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextRoute } = new Navigation();

  const [activeTab, setActiveTab] = useState<DealerTab>('suggestions');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingDealers, setFetchingDealers] = useState(false);
  const [geocodingResolved, setGeocodingResolved] = useState(false);
  const [customerCoords, setCustomerCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const dealerList = carData?.dealerList || '';
  const tenant = carData?.tenant || '';

  // Fetch dealers on mount
  useEffect(() => {
    if (dealerList && tenant) {
      setFetchingDealers(true);
      dispatch(fetchDealersList({ dealerList, tenant })).finally(() => {
        setFetchingDealers(false);
      });
    }
  }, [tenant, dispatch, dealerList]);

  const getCustomerAddress = useCallback(() => {
    const customer = activeApplication?.customer;
    const privateAddress =
      customer?.private?.person?.contact_details?.addresses?.[0];
    const companyAddress =
      customer?.company?.contact_details?.addresses?.[0] ||
      customer?.individual?.contact_details?.addresses?.[0];

    const address = privateAddress || companyAddress;
    if (!address) return null;

    return {
      street: address.street_line || '',
      postalCode: address.postal_code || '',
      city: address.locality || '',
      country: address.country_code || '',
    };
  }, [activeApplication]);

  // Geocode customer address to get coordinates Legacy
  // TODO : Switch to the new Geocode API once it's available
  useEffect(() => {
    const address = getCustomerAddress();
    if (!address) {
      setGeocodingResolved(true);
      return;
    }
    const addressString = `${address.street} ${address.postalCode} ${address.city}`;

    fetch(`/gc-legacy?address=${encodeURIComponent(addressString)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Geocoding failed with HTTP status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data?.status !== 'OK' || !data.results?.[0]) {
          throw new Error(
            `Geocoding failed with status ${data?.status ?? 'UNKNOWN'}`,
          );
        }
        const location = data.results[0].geometry?.location;
        if (
          !Number.isFinite(location?.lat) ||
          !Number.isFinite(location?.lng) ||
          Math.abs(location.lat) > 90 ||
          Math.abs(location.lng) > 180
        ) {
          throw new Error('Geocoding returned invalid coordinates');
        }
        setCustomerCoords({ lat: location.lat, lng: location.lng });
      })
      .catch((error) => {
        setCustomerCoords(null);
        console.error('Geocoding failed', error);
        // show snackbar or some user notification about the geocoding failure
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: false,
            notificationHasWarning: true,
            notificationTitle: 'Standort konnte nicht ermittelt werden',
            notificationBody:
              'Deine Adresse konnte nicht geortet werden. Bitte wähle einen Händler aus der Liste aus.',
          }),
        );
        errorLogging(
          error instanceof Error ? error : new Error('Geocoding failed'),
        );
      })
      .finally(() => {
        setGeocodingResolved(true);
      });
  }, [getCustomerAddress, dispatch]);

  // Geocode customer address to get coordinates
  /*
  useEffect(() => {
    const address = getCustomerAddress();
    if (!address) {
      setGeocodingResolved(true);
      return;
    }

    const params = new URLSearchParams({
      street: address.street || '',
      zipCode: address.postalCode || '',
      city: address.city || '',
      country: address.country || '',
    });



    fetch(`/geocode?${params.toString()}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Geocoding failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setCustomerCoords(data);
        }
      })
      .catch((error) => {
        setActiveTab('search');
        // show snackbar or some user notification about the geocoding failure
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: false,
            notificationHasWarning: true,
            notificationTitle: 'Standort konnte nicht ermittelt werden',
            notificationBody:
              'Deine Adresse konnte nicht geortet werden. Bitte wähle einen Händler aus der Liste aus.',
          }),
        );
        errorLogging(
          error instanceof Error ? error : new Error('Geocoding failed'),
        );
      })
      .finally(() => {
        setGeocodingResolved(true);
      });
  }, [getCustomerAddress, dispatch]);
*/

  // Calculate distances and sort dealers
  const dealersWithDistance = useMemo(() => {
    if (!Array.isArray(dealers)) return [];

    return dealers
      .map((dealer) => {
        let distance: number | null = null;
        if (customerCoords && dealer.lat && dealer.lng) {
          distance = haversineDistance(
            customerCoords.lat,
            customerCoords.lng,
            dealer.lat,
            dealer.lng,
          );
        }
        return { ...dealer, distance };
      })
      .sort((a, b) => {
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
  }, [dealers, customerCoords]);

  // Filtered dealers for search
  const filteredDealers = useMemo(() => {
    // Show only dealers within 50 km on the suggestions tab, ensuring at least one is displayed regardless of distance.
    if (activeTab === 'suggestions') {
      const nearby = dealersWithDistance.filter(
        (d) => d.distance !== null && d.distance <= 50,
      );
      return nearby.length > 0 ? nearby : dealersWithDistance.slice(0, 1);
    }
    if (!searchQuery.trim()) return dealersWithDistance;
    const query = searchQuery.toLowerCase();
    return dealersWithDistance.filter(
      (d) =>
        d.name1.toLowerCase().includes(query) ||
        d.street.toLowerCase().includes(query) ||
        d.city.toLowerCase().includes(query) ||
        d.zip_code.includes(query),
    );
  }, [activeTab, dealersWithDistance, searchQuery]);

  // Auto-select nearest dealer if none selected
  useEffect(() => {
    if (!geocodingResolved) return;
    if (!selectedDealer && dealersWithDistance.length > 0) {
      const existingDealer = activeApplication?.offer?.dealer;
      const nearestDealer = dealersWithDistance.find(
        (dealer) => dealer.distance !== null,
      );
      if (existingDealer) {
        const match = dealersWithDistance.find(
          (d) =>
            d.id === existingDealer.id && d.street === existingDealer.street,
        );
        const dealerToSelect = match || nearestDealer;
        if (dealerToSelect) {
          dispatch(setSelectedDealer(dealerToSelect));
        }
      } else if (nearestDealer) {
        dispatch(setSelectedDealer(nearestDealer));
      }
    }
  }, [
    geocodingResolved,
    dealersWithDistance,
    selectedDealer,
    activeApplication,
    dispatch,
  ]);

  const handleSelect = (dealer: Dealer) => {
    dispatch(setSelectedDealer(dealer));
  };

  const handleSubmit = () => {
    if (!activeApplication) return;
    if (!selectedDealer) {
      navigate(nextRoute());
      return;
    }
    setLoading(true);

    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication.uuid,
        path: APIPaths.UPDATE_DEALER,
        inputValue: {
          dealer: {
            address_line1: selectedDealer.name1 || '',
            address_line2: selectedDealer.name2 || '',
            brand: selectedDealer.brand,
            city: selectedDealer.city,
            dealer_id: selectedDealer.id,
            dealer_name: selectedDealer.name1 || '',
            email_address: selectedDealer.email || '',
            lease_man_delivery_id: selectedDealer.lease_man_delivery_id || '',
            name1: selectedDealer.name1 || '',
            name2: selectedDealer.name2 || '',
            origin: 'DealerSearchService - Customer Selected',
            partner_id: selectedDealer.partner_id || '',
            partner: selectedDealer.partner, // e.g., tenant / partner 'ald_marktplatz'
            phone: selectedDealer.phone || '',
            postal_code: selectedDealer.zip_code,
            street: selectedDealer.street,
          },
        },
      }),
    )
      .catch(() => undefined)
      .finally(() => {
        setLoading(false);
        navigate(nextRoute());
      });
  };

  const openMap = (dealer: Dealer) => {
    const address = encodeURIComponent(
      `${dealer.street}, ${dealer.zip_code} ${dealer.city}`,
    );
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${address}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  // Nearest dealer = our recommendation (Figma: promo-label "Empfehlung")
  const nearestDealer = dealersWithDistance[0];
  const recommendedKey =
    nearestDealer?.distance != null ? getDealerKey(nearestDealer) : null;

  const dealerCount = filteredDealers.length;

  return (
    <Layout
      heading='Wähle deinen Händler'
      stepStatus
      className='layout-bar-bottom'
    >
      <div className='flex w-full flex-col gap-6'>
        <p className={`${bodyTextClass} -mt-5! lg:-mt-4!`}>
          Wohin möchtest du dein Fahrzeug geliefert bekommen?
        </p>

        {/* Figma: segmented control "Unsere Vorschläge" | "Händler suchen" */}
        <SegmentedControl
          kind='button'
          variant='secondary-filled'
          size='s'
          width='full'
          name='dealer-tab'
          value={activeTab}
          onChange={(event) => setActiveTab(event.target.value as DealerTab)}
          data-testid='dealer-tabs'
        >
          <SegmentedControl.Option
            value='suggestions'
            data-testid='dealer-tab-suggestions'
          >
            Unsere Vorschläge
          </SegmentedControl.Option>
          <SegmentedControl.Option
            value='search'
            data-testid='dealer-tab-search'
          >
            Händler suchen
          </SegmentedControl.Option>
        </SegmentedControl>

        {activeTab === 'search' && (
          <Search
            name='dealer-search'
            placeholder='Name oder Anschrift eingeben'
            searchLabel='Händler suchen'
            minChars={0}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onSearch={(event) => setSearchQuery(event.target.value)}
            data-testid='dealer-search'
          />
        )}

        <div className='flex w-full flex-col gap-4'>
          {/* Figma: "5 Händler gefunden" (headline-100) - desktop only */}
          <Headline
            variant='100'
            component='p'
            className={`hidden text-(--vel-color-secondary-400)! lg:block ${
              activeTab === 'search' ? 'block!' : ''
            }`}
            data-testid='dealer-count'
          >
            {dealerCount} Händler gefunden
          </Headline>

          {/* Dealer list (Figma: formkit/radiobutton-card, 16px rhythm) */}
          {dealerCount > 0 && (
            <div
              className='flex w-full flex-col gap-4'
              role='radiogroup'
              aria-label='Händler'
              data-testid='dealer-list'
            >
              {filteredDealers.map((dealer) => {
                const key = getDealerKey(dealer);
                return (
                  <DealerCard
                    key={key}
                    name='dealer'
                    value={key}
                    dealer={dealer}
                    distance={dealer.distance}
                    checked={
                      !!selectedDealer && getDealerKey(selectedDealer) === key
                    }
                    recommended={key === recommendedKey}
                    onSelect={handleSelect}
                    onOpenMap={openMap}
                  />
                );
              })}
            </div>
          )}

          {dealerCount === 0 && !dealers?.length && (
            <p className={`${bodyTextClass} py-8! text-center`}>
              {fetchingDealers
                ? 'Händler werden geladen...'
                : 'Aktuell sind keine Händler verfügbar. Du kannst trotzdem fortfahren.'}
            </p>
          )}
          {activeTab === 'search' && dealerCount === 0 && !!dealers?.length && (
            <p className={`${bodyTextClass} py-8! text-center`}>
              Keine Händler gefunden.
            </p>
          )}
        </div>

        <ButtonGroup
          type='back-next'
          className='back-next'
          buttonOneProps={{
            name: 'weiter',
            type: 'submit',
            loading,
            disabled: fetchingDealers || (!!dealers?.length && !selectedDealer),
            onClick: handleSubmit,
            dataTestId: 'next',
            children: 'nächster Schritt',
          }}
        />
      </div>
    </Layout>
  );
};

export default DealerSelection;
